// eslint-disable-next-line no-restricted-imports
import { skipToken, useIsMutating, useQuery, useQueryClient } from "@tanstack/react-query";
import { HttpMethod } from "src/constants/http-method";
import {
    API_WATCHLIST_UNDERLYINGS_STATE_URL,
    API_WATCHLIST_UNDERLYINGS_URL,
    API_WATCHLIST_WIKIFOLIOS_STATE_URL,
    API_WATCHLISTED_WIKIFOLIOS_URL,
    createApiAddWatchlistWikifoliosUrl,
    createApiRemoveWatchlistUnderlyingsUrl,
    createApiRemoveWatchlistWikifoliosUrl,
} from "src/constants/urls";
import { IMutationOptions, useMutation } from "src/lib/tanstack-query/hooks/use-mutation";
import { IErrorResponse } from "./types";
import {
    IAddWatchlistWikifoliosRequest,
    IFollowUnderlyingRequest,
    IGetWatchlistStateResponse,
    IGetWatchlistUnderlyingsStateRequest,
    IGetWatchlistWikiloliosStateRequest,
    IResponse,
} from "./watchlist.api.types";

export const getWatchlistWikifolioQueryKey = (wikifolioId: string) => ["watchlist", "wikifolio", wikifolioId] as const;

export interface IUseWatchlistWikifolioParams {
    wikifolioId: string;
    isOnWatchlistFromServer: boolean;
}

export const useIsOnWatchlistWikifolio = ({ wikifolioId, isOnWatchlistFromServer }: IUseWatchlistWikifolioParams) => {
    const { data: isOnWatchlist } = useQuery<boolean>({ queryKey: getWatchlistWikifolioQueryKey(wikifolioId), queryFn: skipToken });
    return isOnWatchlist ?? isOnWatchlistFromServer;
};

export const useAddWatchlistWikifoliosMutation = (
    wikifolioId: string,
    options?: IMutationOptions<IResponse, IErrorResponse, IAddWatchlistWikifoliosRequest>
) =>
    useMutation<IResponse, IErrorResponse, IAddWatchlistWikifoliosRequest>(createApiAddWatchlistWikifoliosUrl(wikifolioId), {
        ...options,
        meta: { ...options?.meta, nativeMessage: { type: "WIKIFOLIO_WATCHLIST", wikifolioId, isOnWatchlist: true } },
    });

export const useRemoveWatchlistWikifoliosMutation = (wikifolioId: string, options?: IMutationOptions<void, IErrorResponse, void>) => {
    return useMutation<void, IErrorResponse, void>(createApiRemoveWatchlistWikifoliosUrl(wikifolioId), {
        method: HttpMethod.DELETE,
        ...options,
        meta: { ...options?.meta, nativeMessage: { type: "WIKIFOLIO_WATCHLIST", wikifolioId, isOnWatchlist: false } },
    });
};

const toWatchlistErrorResponse = (thrown: unknown): IErrorResponse => {
    const response = thrown as Partial<IErrorResponse & IResponse> | undefined;
    return { message: response?.message ?? response?.error ?? "" };
};

export const useWatchlistWikifolio = ({ wikifolioId, isOnWatchlistFromServer }: IUseWatchlistWikifolioParams) => {
    const queryClient = useQueryClient();
    const queryKey = getWatchlistWikifolioQueryKey(wikifolioId);
    const mutationFilters = { mutationKey: queryKey };
    const isFetching = useIsMutating(mutationFilters) > 0;
    const isOnWatchlist = useIsOnWatchlistWikifolio({ wikifolioId, isOnWatchlistFromServer });

    const { mutatePromise: add } = useAddWatchlistWikifoliosMutation(wikifolioId, {
        mutationKey: queryKey,
        onSuccess: () => queryClient.setQueryData(queryKey, true),
    });
    const { mutatePromise: remove } = useRemoveWatchlistWikifoliosMutation(wikifolioId, {
        mutationKey: queryKey,
        onSuccess: () => queryClient.setQueryData(queryKey, false),
    });

    const hasPendingMutation = () => queryClient.isMutating(mutationFilters) > 0;

    return { isOnWatchlist, isFetching, add, remove, hasPendingMutation };
};

export const useWatchlistWikifolioToggleQuery = (params: IUseWatchlistWikifolioParams) => {
    const { isOnWatchlist, isFetching, add, remove, hasPendingMutation } = useWatchlistWikifolio(params);

    const toggleWatchlist = async () => {
        if (hasPendingMutation()) {
            return null;
        }

        try {
            if (isOnWatchlist) {
                await remove();
            } else {
                await add({});
            }
            return { isError: false as const };
        } catch (error_) {
            return { isError: true as const, error: toWatchlistErrorResponse(error_) };
        }
    };

    return { isOnWatchlist, isFetching, toggleWatchlist };
};

export const useGetWatchlistWikifoliosStateMutation = (
    options?: IMutationOptions<IGetWatchlistStateResponse, void, IGetWatchlistWikiloliosStateRequest>
) => useMutation<IGetWatchlistStateResponse, void, IGetWatchlistWikiloliosStateRequest>(API_WATCHLIST_WIKIFOLIOS_STATE_URL, options);

export const useAddWatchlistUnderlyingsMutation = (options?: IMutationOptions<IResponse, IErrorResponse, IFollowUnderlyingRequest>) =>
    useMutation<IResponse, IErrorResponse, IFollowUnderlyingRequest>(API_WATCHLIST_UNDERLYINGS_URL, {
        ...options,
        meta: { ...options?.meta, nativeMessage: { type: "UNDERLYING_FOLLOW", isFollowed: true } },
    });

export const useRemoveWatchlistUnderlyingsMutation = (underlyingIsin: string, options?: IMutationOptions<IResponse, IErrorResponse, void>) =>
    useMutation<IResponse, IErrorResponse, void>(createApiRemoveWatchlistUnderlyingsUrl(underlyingIsin), {
        method: HttpMethod.DELETE,
        ...options,
        meta: { ...options?.meta, nativeMessage: { type: "UNDERLYING_FOLLOW", isin: underlyingIsin, isFollowed: false } },
    });

export const useGetWatchlistUnderlyingsStateMutation = (
    options?: IMutationOptions<IGetWatchlistStateResponse, void, IGetWatchlistUnderlyingsStateRequest>
) => useMutation<IGetWatchlistStateResponse, void, IGetWatchlistUnderlyingsStateRequest>(API_WATCHLIST_UNDERLYINGS_STATE_URL, options);

export const useToggleIsSelfInvestedMutation = (wikifolioId: string, options?: IMutationOptions<IResponse, IErrorResponse, boolean>) =>
    useMutation<IResponse, IErrorResponse, boolean>(`${API_WATCHLISTED_WIKIFOLIOS_URL}/${wikifolioId}/invested`, {
        method: HttpMethod.PUT,
        ...options,
    });

export const useFollowUnderlyingMutation = (options?: IMutationOptions<IResponse, IErrorResponse, IFollowUnderlyingRequest>) =>
    useAddWatchlistUnderlyingsMutation(options);

export const useUnfollowUnderlyingMutation = (underlyingIsin: string, options?: IMutationOptions<IResponse, IErrorResponse, void>) =>
    useRemoveWatchlistUnderlyingsMutation(underlyingIsin, options);
